import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertTriangle, HelpCircle, Layers, RefreshCw, X } from 'lucide-react'
import type {
  BulkCreateResponse,
  CaseEndpoint,
  QuestionTemplate,
} from '../../../types/case-container'
import {
  CONNECTION_TYPES,
  MAX_PREVIEW_TARGETS,
  QUESTION_PREVIEW_LENGTH,
  RELATIONSHIP_STATUSES,
  groupConnectionTypes,
} from './bulk-create-link-modal.constants'

export function ModalHeader({
  totalRelationships,
  onClose,
  t,
}: {
  totalRelationships: number
  onClose: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fuchsia-100 dark:bg-fuchsia-950/50">
          <Layers className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('infrastructure.bulkCreate.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('infrastructure.bulkCreate.relationshipCount', { count: totalRelationships })}
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function SuccessBanner({
  result,
  onClose,
  t,
}: {
  result: BulkCreateResponse
  onClose: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/30">
      <p className="text-sm text-green-700 dark:text-green-400">
        {t('infrastructure.bulkCreate.successCount', { count: result.createdCount })}
      </p>
      <button
        onClick={onClose}
        className="mt-2 text-sm text-green-600 hover:underline dark:text-green-400"
      >
        {t('actions.close')}
      </button>
    </div>
  )
}

export function SourceTargetsPreview({
  sourceEndpoint,
  targetEndpoints,
  t,
}: {
  sourceEndpoint: CaseEndpoint
  targetEndpoints: CaseEndpoint[]
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="mb-2 text-xs tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('infrastructure.bulkCreate.sourceEndpoint')}
      </p>
      <span className="rounded bg-fuchsia-100 px-2 py-1 text-sm font-medium text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-400">
        {sourceEndpoint.hostname}
      </span>
      <p className="mt-3 mb-2 text-xs tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {t('infrastructure.bulkCreate.targetEndpoints', { count: targetEndpoints.length })}
      </p>
      <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto">
        {targetEndpoints.slice(0, MAX_PREVIEW_TARGETS).map((ep) => (
          <span
            key={ep.id}
            className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-400"
          >
            {ep.hostname}
          </span>
        ))}
        {targetEndpoints.length > MAX_PREVIEW_TARGETS && (
          <span className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400">
            +{targetEndpoints.length - MAX_PREVIEW_TARGETS} {t('infrastructure.bulkCreate.more')}
          </span>
        )}
      </div>
    </div>
  )
}

export function LargeBatchWarning({
  totalRelationships,
  t,
}: {
  totalRelationships: number
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
      <p className="text-sm text-amber-700 dark:text-amber-400">
        {t('infrastructure.bulkCreate.largeBatchWarning', { count: totalRelationships })}
      </p>
    </div>
  )
}

export function ErrorBanner({ error }: { error: string }): ReactElement {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
    </div>
  )
}

export function ConnectionTypeSelect({
  connectionType,
  setConnectionType,
  t,
}: {
  connectionType: string
  setConnectionType: (v: string) => void
  t: TFunction<'cases'>
}): ReactElement {
  const groupedTypes = groupConnectionTypes()
  const currentDescription = CONNECTION_TYPES.find(
    (type) => type.value === connectionType
  )?.description
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.bulkCreate.connectionType')}
      </label>
      <select
        value={connectionType}
        onChange={(e) => {
          setConnectionType(e.target.value)
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        {Object.entries(groupedTypes).map(([category, types]) => (
          <optgroup key={category} label={category}>
            {types.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{currentDescription}</p>
    </div>
  )
}

export function StatusButtons({
  status,
  setStatus,
  t,
}: {
  status: string
  setStatus: (v: string) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.bulkCreate.status')}
      </label>
      <div className="flex gap-2">
        {RELATIONSHIP_STATUSES.map((opt) => {
          const isActive = status === opt.value
          const activeClass =
            opt.value === 'open-question'
              ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400'
              : 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/50 text-fuchsia-700 dark:text-fuchsia-400'
          const inactiveClass =
            'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setStatus(opt.value)
              }}
              className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${isActive ? activeClass : inactiveClass}`}
            >
              {opt.value === 'open-question' && <HelpCircle className="mr-1 inline h-3.5 w-3.5" />}
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function LabelInput({
  label,
  setLabel,
  t,
}: {
  label: string
  setLabel: (v: string) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.bulkCreate.label')}{' '}
        <span className="font-normal text-slate-400">
          ({t('infrastructure.bulkCreate.optional')})
        </span>
      </label>
      <input
        type="text"
        value={label}
        onChange={(e) => {
          setLabel(e.target.value)
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.bulkCreate.labelPlaceholder')}
      />
    </div>
  )
}

export function BidirectionalToggle({
  bidirectional,
  setBidirectional,
  t,
}: {
  bidirectional: boolean
  setBidirectional: (v: boolean) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <input
        type="checkbox"
        id="bidirectional"
        checked={bidirectional}
        onChange={(e) => {
          setBidirectional(e.target.checked)
        }}
        className="h-4 w-4 rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500 dark:border-slate-600"
      />
      <div>
        <label
          htmlFor="bidirectional"
          className="text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {t('infrastructure.bulkCreate.bidirectional')}
        </label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('infrastructure.bulkCreate.bidirectionalDesc')}
        </p>
      </div>
    </div>
  )
}

interface OpenQuestionProps {
  templates: QuestionTemplate[]
  investigationNotes: string
  setInvestigationNotes: (v: string) => void
  t: TFunction<'cases'>
}

function groupTemplates(templates: QuestionTemplate[]): Record<string, QuestionTemplate[]> {
  return templates.reduce<Record<string, QuestionTemplate[]>>((acc, tpl) => {
    const cat = tpl.category ?? 'Uncategorised'
    acc[cat] ??= []
    acc[cat].push(tpl)
    return acc
  }, {})
}

function TemplateSelector({
  templates,
  setInvestigationNotes,
  t,
}: Pick<OpenQuestionProps, 'templates' | 'setInvestigationNotes' | 't'>): ReactElement {
  const grouped = groupTemplates(templates)
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs font-medium text-amber-700 dark:text-amber-400">
        {t('infrastructure.bulkCreate.useTemplate')}
      </label>
      <select
        onChange={(e) => {
          const template = templates.find((tpl) => tpl.id === e.target.value)
          if (template) setInvestigationNotes(template.questionText)
        }}
        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-amber-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('infrastructure.bulkCreate.selectTemplate')}</option>
        {Object.entries(grouped).map(([category, categoryTemplates]) => (
          <optgroup key={category} label={category}>
            {categoryTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.questionText.slice(0, QUESTION_PREVIEW_LENGTH)}
                {tpl.questionText.length > QUESTION_PREVIEW_LENGTH ? '...' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  )
}

export function OpenQuestionSection({
  templates,
  investigationNotes,
  setInvestigationNotes,
  t,
}: OpenQuestionProps): ReactElement {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="mb-2 flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
          {t('infrastructure.bulkCreate.openQuestion')}
        </span>
      </div>
      {templates.length > 0 && (
        <TemplateSelector
          templates={templates}
          setInvestigationNotes={setInvestigationNotes}
          t={t}
        />
      )}
      <textarea
        value={investigationNotes}
        onChange={(e) => {
          setInvestigationNotes(e.target.value)
        }}
        rows={2}
        className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-amber-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.bulkCreate.notesPlaceholder')}
      />
    </div>
  )
}

export function NotesField({
  investigationNotes,
  setInvestigationNotes,
  t,
}: {
  investigationNotes: string
  setInvestigationNotes: (v: string) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.bulkCreate.notes')}{' '}
        <span className="font-normal text-slate-400">
          ({t('infrastructure.bulkCreate.optional')})
        </span>
      </label>
      <textarea
        value={investigationNotes}
        onChange={(e) => {
          setInvestigationNotes(e.target.value)
        }}
        rows={2}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={t('infrastructure.bulkCreate.notesPlaceholder')}
      />
    </div>
  )
}

export function FormActions({
  onClose,
  creating,
  validTargetCount,
  totalRelationships,
  t,
}: {
  onClose: () => void
  creating: boolean
  validTargetCount: number
  totalRelationships: number
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={creating || validTargetCount === 0}
        className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
      >
        {creating && <RefreshCw className="h-4 w-4 animate-spin" />}
        {t('infrastructure.bulkCreate.createLinks', { count: totalRelationships })}
      </button>
    </div>
  )
}
